import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface AdminUser {
  email: string;
  name: string;
  role: string;
  lastLogin: string;
}

interface AuthContextType {
  isAdmin: boolean;
  currentUser: AdminUser | null;
  login: (email: string, pass: string) => { success: boolean; error?: string };
  logout: () => void;
  remainingLockoutSeconds: number;
}

const AUTHORIZED_CREDENTIALS: Record<string, { pass: string; name: string; role: string }> = {
  'errorcodelagfix@gmail.com': {
    pass: '@Lashary1924',
    name: 'Abdul Mueed Lashary',
    role: 'Lead Architect & Administrator',
  },
  'aswadammarlashary@gmail.com': {
    pass: '@Lashary1924',
    name: 'Aswad Ammar Lashary',
    role: 'Operations Lead & Administrator',
  },
};

const SESSION_KEY = 'lashary_admin_auth_session_v1';
const ATTEMPTS_KEY = 'lashary_admin_failed_attempts';
const LOCKOUT_KEY = 'lashary_admin_lockout_until';
const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes lockout
const SESSION_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours session validity

const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  currentUser: null,
  login: () => ({ success: false }),
  logout: () => {},
  remainingLockoutSeconds: 0,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [remainingLockoutSeconds, setRemainingLockoutSeconds] = useState<number>(0);

  // Restore authenticated session
  useEffect(() => {
    try {
      const storedSession = localStorage.getItem(SESSION_KEY);
      if (storedSession) {
        const parsed = JSON.parse(storedSession);
        const now = Date.now();
        if (parsed.expiresAt && parsed.expiresAt > now && parsed.user) {
          setCurrentUser(parsed.user);
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    }
  }, []);

  // Lockout countdown timer
  useEffect(() => {
    const checkLockout = () => {
      try {
        const lockoutUntilStr = localStorage.getItem(LOCKOUT_KEY);
        if (lockoutUntilStr) {
          const lockoutUntil = parseInt(lockoutUntilStr, 10);
          const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
          setRemainingLockoutSeconds(remaining);
          if (remaining <= 0) {
            localStorage.removeItem(LOCKOUT_KEY);
            localStorage.removeItem(ATTEMPTS_KEY);
          }
        } else {
          setRemainingLockoutSeconds(0);
        }
      } catch {
        setRemainingLockoutSeconds(0);
      }
    };

    checkLockout();
    const interval = setInterval(checkLockout, 1000);
    return () => clearInterval(interval);
  }, []);

  const login = (email: string, pass: string): { success: boolean; error?: string } => {
    // Check active lockout
    if (remainingLockoutSeconds > 0) {
      return {
        success: false,
        error: `Security lockout active. Please wait ${Math.ceil(remainingLockoutSeconds / 60)} minute(s) before retrying.`,
      };
    }

    const normalizedEmail = email.trim().toLowerCase();
    const authorized = AUTHORIZED_CREDENTIALS[normalizedEmail];

    if (!authorized || authorized.pass !== pass) {
      // Record failed attempt
      try {
        const currentAttempts = parseInt(localStorage.getItem(ATTEMPTS_KEY) || '0', 10) + 1;
        localStorage.setItem(ATTEMPTS_KEY, currentAttempts.toString());

        if (currentAttempts >= MAX_FAILED_ATTEMPTS) {
          const lockoutUntil = Date.now() + LOCKOUT_DURATION_MS;
          localStorage.setItem(LOCKOUT_KEY, lockoutUntil.toString());
          setRemainingLockoutSeconds(Math.ceil(LOCKOUT_DURATION_MS / 1000));
          return {
            success: false,
            error: 'Maximum failed attempts exceeded. Security lockout activated for 15 minutes.',
          };
        }

        const remainingAttempts = MAX_FAILED_ATTEMPTS - currentAttempts;
        return {
          success: false,
          error: `Invalid administrative credentials. ${remainingAttempts} attempt(s) remaining before security lockout.`,
        };
      } catch {
        return { success: false, error: 'Authentication processing error.' };
      }
    }

    // Success - reset failed counters
    try {
      localStorage.removeItem(ATTEMPTS_KEY);
      localStorage.removeItem(LOCKOUT_KEY);
      setRemainingLockoutSeconds(0);

      const user: AdminUser = {
        email: normalizedEmail,
        name: authorized.name,
        role: authorized.role,
        lastLogin: new Date().toISOString(),
      };

      const session = {
        user,
        expiresAt: Date.now() + SESSION_DURATION_MS,
      };

      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      setCurrentUser(user);
      return { success: true };
    } catch {
      return { success: false, error: 'Failed to persist administrative session.' };
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAdmin: !!currentUser,
        currentUser,
        login,
        logout,
        remainingLockoutSeconds,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
